// Legacy validation-message identifiers set as the `message` string by vee-validate's
// yup/zod schemas (see schemas/**). Each one maps to a semantic key in the `validation`
// i18n namespace — components resolve the key (and any interpolation params) at render
// time via `useValidationMessage()` (composables/useValidationMessage.ts), which calls
// `t(...)` so the displayed text tracks the active locale.
const VALIDATION_MESSAGE_KEYS: Record<string, string> = {
  'common.validation.mandatory': 'validation.mandatory',
  'common.validation.minChars': 'validation.minChars',
  'common.validation.maxChars': 'validation.maxChars',
  'common.validation.minDetails': 'validation.minDetails',
  'common.validation.invalidPhone': 'validation.invalidPhone',
  'common.validation.invalidEmail': 'validation.invalidEmail',
  'common.validation.passwordLength': 'validation.passwordLength',
  'common.validation.passwordsDoNotMatch': 'validation.passwordsDoNotMatch',
};

// `nameValidationSchema` (schemas/common.ts) enforces this minimum length. Kept here —
// not duplicated as a second magic number — so `validation.minChars`'s `{min}`
// placeholder can be interpolated correctly wherever that message is displayed.
export const NAME_MIN_LENGTH = 2;

const VALIDATION_MESSAGE_PARAMS: Partial<Record<string, Record<string, number>>> = {
  'validation.minChars': { min: NAME_MIN_LENGTH },
};

export type ResolvedValidationMessageKey = {
  key: string;
  params?: Record<string, number>;
  /** False when `key` isn't a known identifier — callers should display it as-is. */
  translate: boolean;
};

/**
 * Resolve a validation-message identifier (a legacy `common.validation.*` key emitted by
 * a schema's `message`) to its `validation.*` i18n key plus any interpolation params.
 * Unknown identifiers (including schemas that already pass a blank/plain string, e.g.
 * `countryValidationSchema`'s `phone_code`) are returned unchanged with `translate: false`
 * — callers must NOT run those through `t()`, or vue-i18n will warn about a missing key.
 */
export const resolveValidationMessageKey = (key: string | undefined | null): ResolvedValidationMessageKey => {
  if (!key) return { key: '', translate: false };

  const i18nKey = VALIDATION_MESSAGE_KEYS[key];
  if (!i18nKey) return { key, translate: false };

  return { key: i18nKey, params: VALIDATION_MESSAGE_PARAMS[i18nKey], translate: true };
};
